import { Supplement } from "../../entriesAPI";
import { SupplementDefinition } from "../../../supplements/supplementAPI";
import { shouldEnforceNegativeValue } from "./helpers";

export interface SupplementInputAllowance {
  allowsValue: boolean;
  allowsTimeRange: boolean;
}

const hasAmountValue = (amount: Supplement["amount"]) =>
  amount !== undefined && amount !== null && amount !== "";

export const getSupplementInputAllowance = (
  definition?: SupplementDefinition | null,
): SupplementInputAllowance => {
  if (definition?.inputTypes) {
    return {
      allowsValue: definition.inputTypes.includes("VALUE"),
      allowsTimeRange: definition.inputTypes.includes("TIME_RANGE"),
    };
  }

  if (definition?.type === "TIMERANGE") {
    return { allowsValue: true, allowsTimeRange: true };
  }

  if (definition?.type === "ON_OFF" || !definition?.type) {
    return { allowsValue: false, allowsTimeRange: false };
  }

  return { allowsValue: true, allowsTimeRange: false };
};

export const initialSupplementInput = (
  definition: SupplementDefinition | undefined,
  entry: { start: number; end: number },
): Pick<Supplement, "amount" | "time"> => {
  const { allowsValue, allowsTimeRange } =
    getSupplementInputAllowance(definition);

  if (allowsTimeRange) {
    return { time: { start: entry.start, end: entry.end } };
  }

  if (allowsValue && definition?.type === "TIMERANGE") {
    return { amount: "" };
  }

  return {};
};

const isAmountValid = (supplement: Supplement) => {
  if (!hasAmountValue(supplement.amount)) return false;

  const numericAmount = Number(supplement.amount);
  if (Number.isNaN(numericAmount) || numericAmount === 0) return false;

  if (shouldEnforceNegativeValue(supplement.code)) {
    return numericAmount < 0;
  }

  return numericAmount > 0;
};

const isTimeRangeValid = (
  supplement: Supplement,
  entryStart: number,
  entryEnd: number,
) => {
  const time = supplement.time;
  if (!time || time.start === undefined || time.end === undefined) return false;

  return (
    time.start < time.end && time.start >= entryStart && time.end <= entryEnd
  );
};

export const supplementInputHasError = (
  supplement: Supplement,
  definition: SupplementDefinition | undefined,
  entryStart: number,
  entryEnd: number,
): boolean => {
  const { allowsValue, allowsTimeRange } =
    getSupplementInputAllowance(definition);

  if (!allowsValue && !allowsTimeRange) return false;

  const amountValid = isAmountValid(supplement);
  const timeValid = isTimeRangeValid(supplement, entryStart, entryEnd);

  if (allowsValue && !allowsTimeRange) return !amountValid;
  if (allowsTimeRange && !allowsValue) return !timeValid;
  if (hasAmountValue(supplement.amount)) return !amountValid;

  return !timeValid;
};

export const supplementWithAllowedInputs = (
  supplement: Supplement,
  definition?: SupplementDefinition | null,
): Supplement => {
  if (!definition) return supplement;

  const { allowsValue, allowsTimeRange } =
    getSupplementInputAllowance(definition);
  const next: Supplement = { ...supplement };

  if (!allowsValue) next.amount = null;
  if (!allowsTimeRange) next.time = null;

  if (allowsValue && allowsTimeRange && hasAmountValue(next.amount)) {
    next.time = null;
  }

  return next;
};

export const supplementsWithAllowedInputs = (
  supplements: Supplement[],
  definitions?: Record<
    string,
    { definition?: SupplementDefinition } | undefined
  >,
): Supplement[] =>
  supplements.map((supplement) =>
    supplementWithAllowedInputs(
      supplement,
      definitions?.[supplement.code]?.definition,
    ),
  );
